import { FC, useMemo } from "react";

import { Label, Listbox, ListboxButton, Transition } from "@headlessui/react";
import { ChevronDown, Building2 } from "lucide-react";

import { useAppDispatch, useAppSelector, getCompany } from "../../store";
import { useApi } from "../../api";
import { transitionSimpleLeaveStyles } from "../../ui/layout/Transition/constants";
import {
  LARGE_ICON_SIZE_STYLES,
  MEDIUM_ICON_SIZE_STYLES,
} from "../../ui/styles";
import { ListboxDropdown } from "../../ui/layout/ListboxDropdown";

export const CompaniesListDropdown: FC = () => {
  const status = useAppSelector((state) => state.companies.status);
  const companyLoading = useAppSelector((state) => state.company.loading);
  const company = useAppSelector((state) => state.company.company);
  const { companies } = useAppSelector((state) => state.companies);

  const selected = useMemo(
    () => ({ id: company?.id, name: company?.name }),
    [company?.id, company?.name],
  );

  const dispatch = useAppDispatch();
  const { getApi } = useApi();

  return (
    <Listbox
      value={selected}
      onChange={async (value) => {
        if (!value) return;

        const id = value.id;
        if (!id) return;
        const api = await getApi();
        dispatch(getCompany({ api, value: id }));
      }}
    >
      {({ open }) => (
        <div
          className={`relative w-full lg:w-64 ${
            status !== "fulfilled" ? "cursor-not-allowed" : ""
          }`}
        >
          <ListboxButton
            className={`grid h-12 w-full cursor-pointer grid-cols-[1fr_24px] gap-3 rounded-md bg-stone-100 py-1 pl-3 pr-2 text-left shadow-sm focus:border-stone-500 focus:outline-none focus:ring-1 focus:ring-stone-500 dark:bg-stone-800 sm:grid-cols-[24px_1fr_24px] *:truncate ${
              status !== "fulfilled" ? "pointer-events-none" : ""
            }`}
          >
            <Building2
              className={`hidden self-center text-stone-500 sm:inline-block ${LARGE_ICON_SIZE_STYLES}`}
            />
            <div className="inline">
              <Label className="block text-xs text-stone-500">Company</Label>
              <span
                className={`text-md block truncate text-stone-900 dark:text-stone-50 ${
                  status === "loading" ? "animate-pulse" : ""
                }`}
              >
                {status === "fulfilled" && !companyLoading
                  ? (selected?.name ?? "Unknown company")
                  : "Loading..."}
              </span>
            </div>
            <span className="self-center pr-2">
              <ChevronDown
                className={`text-stone-400 ${MEDIUM_ICON_SIZE_STYLES}`}
              />
            </span>
          </ListboxButton>

          <Transition show={open} as="div" {...transitionSimpleLeaveStyles}>
            <ListboxDropdown listItems={companies} />
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
